/**
 * Multi-Account Management Utilities
 * Central export for all multi-account related utilities
 */

// Encryption Service
export {
  EncryptionService,
  SessionKeyManager,
  type EncryptedData,
  type AccountCredentials
} from './encryptionService';

// Account Storage
export {
  LocalAccountStorage,
  AccountCredentialsHelper,
  accountStorage,
  type StoredAccount,
  type AccountStorage
} from './accountStorage';

// Note: AccountManager and CentralizedAccountManager services have been removed
// during the auth service consolidation (Task 8.7).
// Multi-account functionality is now handled through Redux thunks and the
// consolidated MultiAccountService in app/services/auth/consolidated/

// Type definitions for account change events (preserved for compatibility)
export type AccountChangeEvent = {
  type: 'account_added' | 'account_removed' | 'account_switched';
  accountId: string;
  previousAccountId?: string;
  newAccountId?: string;
  affectedPages: string[];
  requiresPageRefresh: boolean;
  timestamp: number;
  source: string;
};

export type AccountChangeCallback = (event: AccountChangeEvent) => void;
export type UnsubscribeFunction = () => void;

// Note: GlobalAccountEventSystem and AccountOperationCoordinator services have been removed
// during the auth service consolidation (Task 8.7).
// Event handling is now managed through Redux state changes.

// Account Metadata
export {
  AccountMetadataHelper,
  type AccountMetadata,
  type AccountUsageStats
} from './accountMetadata';

// Duplicate Prevention
export {
  DuplicateAccountPrevention,
  type DuplicateCheckResult,
  type AccountConflictResolution
} from './duplicateAccountPrevention';

// Constants
export {
  multiAccountKeys as MULTI_ACCOUNT_KEYS,
  multiAccountConfig as MULTI_ACCOUNT_CONFIG,
  accountSwitcherUi as ACCOUNT_SWITCHER_UI,
  multiAccountErrors as MULTI_ACCOUNT_ERRORS,
  multiAccountMessages as MULTI_ACCOUNT_MESSAGES,
  type AccountStatus,
  type AccountAction,
  type MigrationStatus,
  type AccountValidationResult
} from '../../../constants/multiAccountConstants';

/**
 * Initialize multi-account system with session validation
 * Should be called during application startup
 * 
 * Note: This function has been simplified after auth service consolidation (Task 8.7).
 * Multi-account functionality is now handled through Redux thunks and consolidated services.
 */
export const initializeMultiAccountSystem = async (): Promise<void> => {
  try {
    console.log('Initializing multi-account system...');

    // Check encryption support
    if (!crypto || !crypto.subtle) {
      console.error('Multi-account system requires Web Crypto API support');
      throw new Error('Encryption not supported');
    }

    // Initialize session key manager
    const { SessionKeyManager } = await import('./encryptionService');
    SessionKeyManager.getSessionId();

    // Validate existing accounts against current session key
    await validateAccountSessionCompatibility();

    console.log('Multi-account system initialized successfully');
  } catch (error) {
    console.error('Failed to initialize multi-account system:', error);
    throw error;
  }
};

/**
 * Validate that existing accounts can be decrypted with current session key
 * If not, clean up corrupted accounts to prevent login retry loops
 */
const validateAccountSessionCompatibility = async (): Promise<void> => {
  try {
    const { accountStorage, AccountCredentialsHelper } = await import('./accountStorage');
    const accounts = await accountStorage.getAllAccounts();
    
    if (accounts.length === 0) {
      console.log('No accounts to validate');
      return;
    }

    console.log(`Validating ${accounts.length} accounts for session compatibility...`);
    
    const corruptedAccounts: string[] = [];
    
    // Test each account for decryption compatibility
    for (const account of accounts) {
      try {
        // Try to decrypt the account with current session key
        await AccountCredentialsHelper.extractAuthSecret(account);
        console.log(`Account ${account.id} (${account.email}) is compatible`);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.warn(`Account ${account.id} (${account.email}) is corrupted:`, errorMessage);
        corruptedAccounts.push(account.id);
      }
    }
    
    // Clean up corrupted accounts
    if (corruptedAccounts.length > 0) {
      console.log(`Cleaning up ${corruptedAccounts.length} corrupted accounts...`);
      
      for (const accountId of corruptedAccounts) {
        try {
          await accountStorage.removeAccount(accountId);
          console.log(`Removed corrupted account: ${accountId}`);
        } catch (error) {
          console.error(`Failed to remove corrupted account ${accountId}:`, error);
        }
      }
      
      // If all accounts were corrupted, force reset the session
      const remainingAccounts = await accountStorage.getAllAccounts();
      if (remainingAccounts.length === 0) {
        console.log('All accounts were corrupted, force resetting session...');
        const { SessionKeyManager } = await import('./encryptionService');
        SessionKeyManager.forceResetSession();
      }
      
      console.log(`Session validation completed: ${corruptedAccounts.length} corrupted accounts removed`);
    } else {
      console.log('All accounts are compatible with current session');
    }
  } catch (error) {
    console.error('Failed to validate account session compatibility:', error);
    // Don't throw error to avoid breaking initialization
  }
};

/**
 * Cleanup multi-account system
 * Should be called during application shutdown or logout
 * 
 * Note: This function has been simplified after auth service consolidation (Task 8.7).
 */
export const cleanupMultiAccountSystem = async (): Promise<void> => {
  try {
    console.log('Cleaning up multi-account system...');

    // Clear session key
    const { SessionKeyManager } = await import('./encryptionService');
    SessionKeyManager.clearSession();

    console.log('Multi-account system cleanup completed');
  } catch (error) {
    console.error('Failed to cleanup multi-account system:', error);
  }
};